<template>
    <div>
        <!-- Filter Section -->
        <div class="card mb-3">
            <div class="card-header">
                <h5 class="mb-0">{{ trans('Filter') }}</h5>
            </div>
            <div class="card-body">
                <div class="row">
                    <!-- Start Date -->
                    <div class="col-md-3">
                        <label for="date_start">{{ trans('Start Date') }}</label>
                        <vue-flatpickr
                            :modelValue="filter.date_start"
                            :config="{
                                allowInput: true,
                                altFormat: 'd/m/Y',
                                altInput: true,
                                dateFormat: 'Y-m-d',
                            }"
                            :placeholder="trans('Select start date')"
                            name="date_start"
                            class="form-control bg-white"
                            autocomplete="off"
                            @update:modelValue="onDateStartChange"
                        >
                        </vue-flatpickr>
                    </div>

                    <!-- End Date -->
                    <div class="col-md-3">
                        <label for="date_end">{{ trans('End Date') }}</label>
                        <vue-flatpickr
                            :modelValue="filter.date_end"
                            :config="{
                                allowInput: true,
                                altFormat: 'd/m/Y',
                                altInput: true,
                                dateFormat: 'Y-m-d',
                            }"
                            :placeholder="trans('Select end date')"
                            name="date_end"
                            class="form-control bg-white"
                            autocomplete="off"
                            @update:modelValue="onDateEndChange"
                        >
                        </vue-flatpickr>
                    </div>

                    <!-- Departments -->
                    <div class="col-md-4">
                        <label>{{ trans('Departments') }}</label>
                        <div class="departments-container">
                            <template v-if="loadingDepartments">
                                <div class="text-center py-2">
                                    <i class="icofont-spinner"></i>
                                    {{ trans('Loading...') }}
                                </div>
                            </template>
                            <template v-else-if="departments.length === 0">
                                <div class="text-muted text-center py-2">
                                    {{ trans('No departments found') }}
                                </div>
                            </template>
                            <template v-else>
                                <div class="form-check mb-1">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="select-all-departments"
                                        :checked="areAllDepartmentsSelected"
                                        @change="toggleAllDepartments"
                                    >
                                    <label class="form-check-label font-weight-bold" for="select-all-departments">
                                        {{ trans('Select All') }}
                                    </label>
                                </div>
                                <hr class="my-2">
                                <div class="departments-list">
                                    <div
                                        v-for="department in departments"
                                        :key="department.id"
                                        class="form-check mb-1"
                                    >
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            :id="'dept-' + department.id"
                                            :value="department.id"
                                            v-model="filter.selected_departments"
                                        >
                                        <label class="form-check-label" :for="'dept-' + department.id">
                                            {{ department.text }}
                                        </label>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <small class="text-muted">
                            {{ selectedDepartmentsText }}
                        </small>
                    </div>

                    <!-- Export Button -->
                    <div class="col-md-2 d-flex align-items-end">
                        <button
                            @click="exportToExcel"
                            :disabled="loadingExport || !filter.date_start || !filter.date_end"
                            class="btn btn-primary w-100"
                        >
                            <i class="icofont-download" v-if="!loadingExport" style="font-size: 14px;"></i>
                            <i class="icofont-spinner" v-else style="font-size: 14px;"></i>
                            {{ trans('Create XLS') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueFlatpickr from 'vue-flatpickr-component';
import { LocalDate } from '@js-joda/core';

export default {
    components: {
        VueFlatpickr,
    },
    data() {
        return {
            filter: {
                date_start: '',
                date_end: '',
                selected_departments: [],
            },
            departments: [],
            loadingDepartments: false,
            loadingExport: false,
        };
    },
    computed: {
        areAllDepartmentsSelected() {
            return this.departments.length > 0 && 
                   this.filter.selected_departments.length === this.departments.length;
        },
        selectedDepartmentsText() {
            if (this.filter.selected_departments.length === 0) {
                return trans('No departments selected (all will be included)');
            } else if (this.filter.selected_departments.length === this.departments.length) {
                return trans('All departments selected');
            } else {
                return trans(':count departments selected', { count: this.filter.selected_departments.length });
            }
        },
    },
    mounted() {
        this.initializeDates();
        this.fetchDepartments();
    },
    methods: {
        initializeDates() {
            // Set start date to first day of current year
            const now = LocalDate.now();
            const startOfYear = now.withDayOfYear(1);
            const endOfYear = now.withDayOfYear(now.lengthOfYear());

            this.filter.date_start = startOfYear.toString();
            this.filter.date_end = endOfYear.toString();
        },
        onDateStartChange(value) {
            this.filter.date_start = value;
            
            // Validate date range: if start date is after end date, adjust end date
            if (value && this.filter.date_end) {
                const startDate = LocalDate.parse(value);
                const endDate = LocalDate.parse(this.filter.date_end);
                
                if (startDate.isAfter(endDate)) {
                    // Set end date to start date + 1 day
                    this.filter.date_end = startDate.plusDays(1).toString();
                }
            }
        },
        onDateEndChange(value) {
            this.filter.date_end = value;
            
            // Validate date range: if end date is before start date, adjust start date
            if (value && this.filter.date_start) {
                const startDate = LocalDate.parse(this.filter.date_start);
                const endDate = LocalDate.parse(value);
                
                if (endDate.isBefore(startDate)) {
                    // Set start date to end date - 1 day
                    this.filter.date_start = endDate.minusDays(1).toString();
                }
            }
        },
        toggleAllDepartments() {
            if (this.areAllDepartmentsSelected) {
                this.filter.selected_departments = [];
            } else {
                this.filter.selected_departments = this.departments.map(dept => dept.id);
            }
        },
        async fetchDepartments() {
            this.loadingDepartments = true;
            try {
                const response = await axios.get(route('export-withholding-tax.departments'));
                this.departments = response.data;
            } catch (error) {
                console.error('Error fetching departments:', error);
                // You might want to show a toast notification here
            } finally {
                this.loadingDepartments = false;
            }
        },
        async exportToExcel() {
            this.loadingExport = true;
            try {
                const response = await axios.post(route('export-withholding-tax.export'), {
                    date_start: this.filter.date_start,
                    date_end: this.filter.date_end,
                    department_ids: this.filter.selected_departments,
                }, {
                    responseType: 'blob'
                });

                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                
                // Extract filename from response headers or use default
                const contentDisposition = response.headers['content-disposition'];
                let filename = 'Export korting bedrijfsvoorheffing.xlsx';
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                }
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error exporting to Excel:', error);
                // You might want to show a toast notification here
            } finally {
                this.loadingExport = false;
            }
        },
    },
};
</script>

<style scoped>
.departments-container .form-check-label {
    cursor: pointer;
    user-select: none;
}
</style> 